import type { InventoryRowEdit, PoolManagementView } from '..';
import type { InventoryParseResult } from 'src/admin/models/PoolManagementModel/upload/parseInventoryCsv';

export type UploadWizardStep = 'drop' | 'review' | 'confirm' | 'done';

/**
 * Tabs in the pool detail view. `permissions` is the default landing
 * tab per phase-2 design. The `change-log` and `revenue` values are
 * reserved here so the type compiles when those tabs land in future
 * tickets.
 */
export type PoolDetailTab = 'permissions' | 'lab-pool' | 'change-log' | 'revenue';

export type UploadCategory = 'added' | 'updated' | 'removed' | 'errors' | 'unchanged';

/**
 * Page size for the LAB pool inventory table. Numeric values mirror the
 * design's "25 / 50 / 100" options; `'all'` disables pagination by
 * showing every filtered row on a single page.
 */
export type InventoryRowsPerPage = 25 | 50 | 100 | 'all';

/**
 * Action category filter for the Change Log tab. The backend's `action`
 * code can't reliably distinguish a delete from a quantity reduction
 * (both can land as the same code), so the filter is sign-based: any
 * entry with `quantity > 0` is an increase, `quantity < 0` a decrease.
 */
export type ChangeLogActionFilter = 'all' | 'increase' | 'decrease';

/**
 * Time-range filter for the Change Log tab. `today` and the Last-N-days
 * values are relative to the current local day; `custom` is reserved
 * for a future from/to date picker that's not yet wired.
 */
export type ChangeLogTimeRange = 'today' | 'last-7-days' | 'last-30-days' | 'custom';

/**
 * How the upload's diff is applied against the live pool.
 *
 * - `'merge'`: insert new symbols and update overlapping ones. Symbols
 *   already in the pool that are not in the file are left untouched.
 *   This is the only mode available in phase 1.
 * - `'replace'`: in addition to `merge` behavior, delete pool symbols
 *   that are not present in the file. Not yet exposed in the UI;
 *   reserved so future work can wire it up without re-plumbing the
 *   state and the apply path.
 */
export type UploadMode = 'merge' | 'replace';

export interface UploadFileInfo {
    name: string;
    size: number;
    uploadedAt: number;
}

export interface PoolManagementState {
    view: PoolManagementView;
    selectedPoolId: string | null;
    poolSearchQuery: string;
    /**
     * Raw text in the inventory symbol-search box. Drives the input
     * value directly so typing stays responsive. The backend query is
     * driven by {@link appliedSymbolSearch}, which the model commits
     * (debounced) from this value.
     */
    symbolSearchQuery: string;
    /**
     * Committed symbol-search term sent to the backend as the `symbol`
     * query parameter. Trails {@link symbolSearchQuery} by a debounce so
     * live typing fires a single backend search instead of one request
     * per keystroke. Empty means "no filter — page the full pool".
     */
    appliedSymbolSearch: string;
    addSymbolDraft: string;
    rowEdits: Record<string, InventoryRowEdit>;
    pendingRowRemovals: ReadonlyArray<string>;
    submitReviewOpen: boolean;
    /**
     * True while a submit is in flight. Lives in reducer state so the
     * transitions are testable and `closeSubmitReview` / pool-switch
     * cleanup can clear it in a single action.
     */
    isSubmitting: boolean;
    /**
     * Last submit-failure message, or null. Cleared on
     * START_SUBMIT, CLEAR_SUBMIT_ERROR, RESET_EDITS, and any
     * navigation that resets the per-pool edit slate.
     */
    submitError: string | null;

    /**
     * Active tab in the pool detail view. Reset to `'permissions'`
     * every time a pool is opened so the user always lands on the
     * permissions tab first.
     */
    activePoolDetailTab: PoolDetailTab;

    /**
     * 1-based current page of the inventory table. Reset to 1 on pool
     * open and whenever the symbol-search query changes so the user is
     * never stranded on an empty page after a filter narrows results.
     */
    inventoryPage: number;
    /** Page size for the inventory table; defaults to 25. */
    inventoryRowsPerPage: InventoryRowsPerPage;

    // --- Change Log tab filters ---
    /** Free-text symbol filter for the Change Log; case-insensitive. */
    changeLogSymbolFilter: string;
    changeLogActionFilter: ChangeLogActionFilter;
    changeLogTimeRange: ChangeLogTimeRange;

    // --- Upload wizard ---
    uploadWizardOpen: boolean;
    uploadStep: UploadWizardStep;
    uploadFile: UploadFileInfo | null;
    uploadParseResult: InventoryParseResult | null;
    uploadCategoryFilter: UploadCategory;
    uploadSymbolFilter: string;
    /**
     * Hardcoded to `'merge'` in phase 1; the reducer resets it on open
     * and close. Reserved so a mode-selector UI can flip this through
     * `SET_UPLOAD_MODE` without further state plumbing.
     */
    uploadMode: UploadMode;
    /**
     * True while an `applyUpload` is in flight. Lives in reducer state
     * so the transitions are testable and `closeUploadWizard` /
     * pool-switch cleanup can clear it in a single action.
     */
    uploadApplying: boolean;
    /** Last upload-failure message, or null. */
    uploadError: string | null;
    /** Last successful apply's category counts; surfaces the "done" screen. */
    uploadAppliedSummary: { added: number; updated: number; removed: number } | null;
}

export const initialPoolManagementState: PoolManagementState = {
    view: 'list',
    selectedPoolId: null,
    poolSearchQuery: '',
    symbolSearchQuery: '',
    appliedSymbolSearch: '',
    addSymbolDraft: '',
    rowEdits: {},
    pendingRowRemovals: [],
    submitReviewOpen: false,
    isSubmitting: false,
    submitError: null,
    activePoolDetailTab: 'permissions',
    inventoryPage: 1,
    inventoryRowsPerPage: 25,
    changeLogSymbolFilter: '',
    changeLogActionFilter: 'all',
    changeLogTimeRange: 'last-7-days',
    uploadWizardOpen: false,
    uploadStep: 'drop',
    uploadFile: null,
    uploadParseResult: null,
    uploadCategoryFilter: 'added',
    uploadSymbolFilter: '',
    uploadMode: 'merge',
    uploadApplying: false,
    uploadError: null,
    uploadAppliedSummary: null,
};
